import { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Notification } from '@progress/kendo-react-notification';
import { Fade } from '@progress/kendo-react-animation';
import { Error } from '@progress/kendo-react-labels';

import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  hideSaveCriteria,
  selectSaveCriteriaVisible,
} from '../../store/slices/save-criteria-modals.slice';
import { RootState } from '../../store';
import { DatasetID, FilterType } from '../../models/filter.model';
import { selectCommonFilters, updateCommonFilters } from '../../store/slices/common-filters.slice';
import {
  selectTempEndDate,
  selectTempStartDate,
  selectTempVisualData,
  selectTempVisualType,
  setAndApplyDateFilter,
} from '../../store/slices/date-filter.slice';
import { selectFilterParent } from '../../store/slices/filter-parent.slice';
import { useCreateUserDataRowMutation } from '../../store/api/user-data.api';
import { NotificationType } from '../../models/notification.model';
import {
  selectSavedFiltersEntries,
  setSavedFilterKey,
  setSavedFiltersEntries,
} from '../../store/slices/saved-filters.slice';
import { UserDataType } from '../../models/user.model';

export function SaveCriteriaModal({ datasetId, onClose }: DatasetID & { onClose: () => void }) {
  const [criteriaName, setCriteriaName] = useState('Saved Criteria #1');
  const [success, setSuccess] = useState(false);
  const [saveCriteria] = useCreateUserDataRowMutation();

  const visible = useAppSelector(selectSaveCriteriaVisible);
  const currentFilters = useAppSelector(selectSavedFiltersEntries);

  const filters = useAppSelector((state: RootState) => selectCommonFilters(state, datasetId));
  const filterParent = useAppSelector(selectFilterParent);

  const activeTab = useAppSelector((state: RootState) => selectTempVisualType(state, datasetId));
  const visualRepresentation = useAppSelector((state: RootState) =>
    selectTempVisualData(state, datasetId)
  );
  const startDate = useAppSelector((state: RootState) => selectTempStartDate(state, datasetId));
  const endDate = useAppSelector((state: RootState) => selectTempEndDate(state, datasetId));

  const dispatch = useAppDispatch();
  const toggleDialog = () => dispatch(hideSaveCriteria());

  const onSaveCriteria = useCallback(async () => {
    const result = await saveCriteria({
      userDataTypeId: UserDataType.SavedCriteria,
      data: {
        filterParent,
        filters,
        dateConfig: {
          activeTab,
          visualRepresentation,
          startDate,
          endDate,
        },
        name: criteriaName,
      },
    });

    if ('data' in result) {
      dispatch(
        setSavedFiltersEntries({
          entries: [
            ...(currentFilters ?? []),
            {
              key: (result.data as number[])[0],
              value: {
                filterParent,
                filters,
                dateConfig: {
                  activeTab,
                  visualRepresentation,
                  startDate,
                  endDate,
                },
                name: criteriaName,
              },
            },
          ],
        })
      );

      const filterData = Object.keys(filters).map((filterKey) => ({
        key: filterKey,
        label: filters[filterKey].label,
        filter: {
          label: filters[filterKey].label,
          value: filters[filterKey].value,
          type: FilterType.AUTOCOMPLETE,
        },
        type: FilterType.AUTOCOMPLETE,
      }));
      dispatch(updateCommonFilters({ datasetId, filters: filterData }));
      dispatch(
        setAndApplyDateFilter({
          datasetId,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          visualData: visualRepresentation,
          visualType: activeTab,
        })
      );
      dispatch(setSavedFilterKey({ datasetId, value: (result.data as number[])[0] }));
    }

    setSuccess(true);
    dispatch(hideSaveCriteria());
    onClose();
  }, [
    filters,
    filterParent,
    saveCriteria,
    activeTab,
    visualRepresentation,
    startDate,
    endDate,
    criteriaName,
    dispatch,
    currentFilters,
    onClose,
    datasetId,
  ]);

  const onChange = (e: InputChangeEvent) => {
    setCriteriaName(e.value);
  };

  const validationMessage = useMemo(() => {
    if (currentFilters?.some((filter) => filter.value.name === criteriaName)) {
      return 'Duplicate name';
    }

    if (!criteriaName || criteriaName.trim() === '') {
      return 'Required field';
    }
  }, [criteriaName, currentFilters]);

  useEffect(() => {
    if (currentFilters) {
      const biggetSavedFilterNumber =
        currentFilters
          .map((filter) => {
            const criteriaNumber = Number(filter.value.name.split('#').at(-1));
            if (isNaN(criteriaNumber)) {
              return 0;
            }

            return criteriaNumber;
          })
          .sort((a, b) => a - b)
          .at(-1) ?? 0;

      setCriteriaName(`Saved Criteria #${biggetSavedFilterNumber + 1}`);
    }
  }, [currentFilters]);

  return (
    <>
      {visible && (
        <Dialog className="save-criteria-dialog" title="Save Criteria" onClose={toggleDialog}>
          <div className="dialog-content-vertical" style={{ width: '450px' }}>
            <Input
              placeholder="Type Criteria Name"
              defaultValue={criteriaName}
              onChange={onChange}
              autoFocus={true}
              label="Save as new"
              valid={!validationMessage}
            />
            {validationMessage && <Error>{validationMessage}</Error>}
          </div>
          <DialogActionsBar layout="center">
            <Button themeColor={'primary'} onClick={onSaveCriteria} disabled={!!validationMessage}>
              Save & Apply
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
      <Fade>
        {success && (
          <Notification
            type={{ style: NotificationType.SUCCESS, icon: false }}
            closable={true}
            onClose={() => setSuccess(false)}
          >
            <span>Criteria successfully saved.</span>
          </Notification>
        )}
      </Fade>
    </>
  );
}
